'use client';

import { useEffect, useState, Suspense } from 'react';
import { usePathname } from 'next/navigation';
import StaticSection from '@/components/common/static-section/StaticSection';
import { useDynamicContent } from '@/hooks/useDynamicContent';
import AnnualReportCategoryPage from '../_components/AnnualReportCategoryPage';
import { findStaticPage } from './static-pages.config';
import { getMenuIdFromSlug } from '@/lib/getMenuIdFromSlug';
import type { Locale } from '@/lib/localized-paths';
import type { MenuId } from '@/lib/enum';
import { usePathTranslation } from '../providers/PathTranslationProvider';
import { useCurrentNavigationNode } from '@/hooks/useCurrentNavigationNode';

interface DynamicPageClientProps {
  params: {
    lang: string;
    slug: string[];
  };
  searchParams: { [key: string]: string | string[] | undefined };
}

export default function DynamicPageClient({ params, searchParams }: DynamicPageClientProps) {
  const pathname = usePathname();
  const [resolvedSearchParams, setResolvedSearchParams] = useState<URLSearchParams | null>(null);
  const [StaticPageComponent, setStaticPageComponent] = useState<React.ComponentType | null>(null);
  const [isStaticPage, setIsStaticPage] = useState<boolean>(false);
  const [checkingStaticPage, setCheckingStaticPage] = useState<boolean>(true);

  // Resolve the slug used for content + static-page detection.
  //
  // Prefer the server-provided canonical slug (params.slug), which always reflects
  // the path after the middleware rewrite (e.g. [about-us, affiliations]).
  // Deriving the slug from the browser URL is unreliable during client-side
  // navigation: when this component mounts, usePathname() may still hold the
  // PREVIOUS page's path (e.g. /id/tentang-kami/sekretaris-perusahaan), which made
  // findStaticPage() match the corporate-secretary pattern and render the wrong
  // page — clicking "Hubungan Afiliasi" in the sidebar kept showing Sekretaris
  // Perusahaan. params.slug is only empty in edge cases, where we fall back to the
  // pathname-derived slug.
  const paramsSlug = params.slug || [];
  const pathSlug = (pathname || '').split('/').filter(Boolean).slice(1); // Remove lang
  const effectiveSlug = paramsSlug.length > 0 ? paramsSlug : pathSlug;
  const slugKey = effectiveSlug.join('/');

  // Automatically detect MenuId from first slug segment for sidebar navigation
  const menuId: MenuId | undefined = getMenuIdFromSlug(
    effectiveSlug?.[0],
    params.lang as Locale
  );

  // Check if this is a static page first (before calling useDynamicContent).
  // Keyed on the slug string so the decision is re-evaluated (and state reset)
  // whenever the route changes, e.g. client-side navigation between dynamic pages.
  useEffect(() => {
    // Reset the previous decision before re-evaluating for the new slug
    setIsStaticPage(false);
    setStaticPageComponent(null);
    setCheckingStaticPage(true);

    const staticPageConfig = findStaticPage(effectiveSlug || []);

    if (staticPageConfig) {
      setIsStaticPage(true);
      // Lazy load the static page component
      staticPageConfig.component().then(module => {
        setStaticPageComponent(() => module.default);
        setCheckingStaticPage(false);
      }).catch(error => {
        console.error('Error loading static page:', error);
        setIsStaticPage(false);
        setCheckingStaticPage(false);
      });
    } else {
      setIsStaticPage(false);
      setCheckingStaticPage(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slugKey]);

  // Convert searchParams to URLSearchParams and add language
  useEffect(() => {
    // Ensure params.lang exists before setting search params
    if (!params.lang) {
      return;
    }

    const urlSearchParams = new URLSearchParams(
      Object.entries(searchParams).reduce((acc, [key, value]) => {
        if (value !== undefined) {
          acc[key] = Array.isArray(value) ? value.join(',') : value;
        }
        return acc;
      }, {} as Record<string, string>)
    );
    
    // Add language parameter for the API
    urlSearchParams.set('lang', params.lang);
    
    setResolvedSearchParams(urlSearchParams);
  }, [searchParams, params.lang]);

  // Use the dynamic content hook ONLY if this is not a static page
  const fallbackParams = new URLSearchParams();
  if (params.lang) {
    fallbackParams.set('lang', params.lang);
  }

  const { data, isLoading, error } = useDynamicContent({
    slug: effectiveSlug || [],
    searchParams: resolvedSearchParams || fallbackParams,
    enabled: !isStaticPage && !checkingStaticPage // Only fetch if NOT a static page
  });
  // Propagate navigation_path_translations to the LanguageSwitcher via context
  const { setTranslations } = usePathTranslation();
  useEffect(() => {
    if (data?.navigationPathTranslations) {
      setTranslations(data.navigationPathTranslations);
    }
    return () => {
      setTranslations(null);
    };
  }, [data?.navigationPathTranslations, setTranslations]);

  const pageTitle = data?.title || "";
  const currentNavigationNode = useCurrentNavigationNode();
  // Avoid sidebar flicker: only decide visibility after dynamic data exists.
  const shouldShowSidebar =
    Boolean(data) &&
    data?.showVerticalSidebar !== false &&
    currentNavigationNode?.show_sidebar !== false;
  const effectiveSidebarMenuId = shouldShowSidebar ? menuId : undefined;
  const effectiveSidebarParentSlug = shouldShowSidebar ? effectiveSlug?.[0] : undefined;

  // If checking for static page, show loading
  if (checkingStaticPage) {
    return (
      <StaticSection
        title=""
        query={{
          data: undefined,
          isLoading: true,
          error: null
        }}
        skeletonCount={3}
        withLayout={true}
      />
    );
  }

  // If this is a static page, render it directly (no API call was made)
  if (isStaticPage && StaticPageComponent) {
    return (
      <Suspense fallback={
        <StaticSection
          title=""
          query={{
            data: undefined,
            isLoading: true,
            error: null
          }}
          skeletonCount={3}
          withLayout={true}
        />
      }>
        <StaticPageComponent />
      </Suspense>
    );
  }

  // Route to appropriate component based on pageType
  if (data?.pageType) {
    switch (data.pageType) {
      case 'annual_report_category':
        return (
          <AnnualReportCategoryPage
            category={data.category!}
            title={pageTitle}
            lang={params.lang}
          />
        );
    }
  }

  // Default: render CMS content
  return (
    <StaticSection
      title={pageTitle}
      query={{
        data: data || undefined,
        // Also treat the brief transition when isLoading hasn't flipped to true yet
        // (enabled just changed false→true) as loading to prevent a blank flash.
        isLoading: isLoading || (!data && !error),
        error
      }}
      skeletonCount={3}
      withLayout={true}
      sidebarMenuId={effectiveSidebarMenuId}
      sidebarParentSlug={effectiveSidebarParentSlug}
    />
  );
}